/*
  options: [{value: x, label: x}, ...]
*/

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { BaseSelect } from './BaseSelect';

// This selector that's a wrapper around BaseSelect is used either in the case you use Formik,
// in which you only provide a String value implicitly through 'field', so we need to format the value
// in a { value, label } object.
// Or it's not in Formik, but you are only passing the value as a string. Again, it has to be formatted.
export const SimpleSelector = (props) => {
  const {
    field,
    value,
    overrideLabel,
    onChange,
    handleSelectorToggled,
    options,
    placeholder,
    outOfRange,
    ...otherProps
  } = props;
  // Map the options array so we can grab the label easily.
  // Wrapped in a useMemo so this only gets created once, unless options changes.
  const optionsMap = useMemo(() => {
    const map = {};
    options.forEach((type) => {
      map[type.value] = type.label;
    });
    return map;
  }, [options]);

  let valueObj;
  let handleSelection;

  // If being used with Formik
  if (field) {
    valueObj = {
      value: field.value,
      label: overrideLabel || optionsMap[field.value],
    };
    handleSelection = (selection) => {
      onChange(field.name, selection.value);
    };
  } else {
    valueObj = { value, label: overrideLabel || optionsMap[value] };
    handleSelection = onChange;
  }

  // valueObj set to null if no value is present so placeholder can take over.
  // null is only loosely equal to itself and undefined.
  if (valueObj.value == null) valueObj = null;

  return (
    <BaseSelect
      value={valueObj}
      onChange={handleSelection}
      placeholder={placeholder}
      onMenuOpen={
        handleSelectorToggled ? () => handleSelectorToggled(true) : null
      }
      onMenuClose={
        handleSelectorToggled ? () => handleSelectorToggled(false) : null
      }
      outOfRange={outOfRange}
      options={options}
      {...otherProps}
    />
  );
};

SimpleSelector.defaultProps = {
  placeholder: 'Select',
  overrideLabel: null,
  handleSelectorToggled: () => {},
  outOfRange: false,
};

SimpleSelector.propTypes = {
  field: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  overrideLabel: PropTypes.string,
  handleSelectorToggled: PropTypes.func,
  placeholder: PropTypes.string,
  outOfRange: PropTypes.bool,
};
